/**
 * Contexto global de Llamadas (WebRTC): mantiene la llamada al cambiar de página
 * y muestra una barra fija "En llamada" con timer e "Ir al Marcador" / "Colgar".
 * Evita que al salir del Marcador se corte la llamada (como en apps de teléfono).
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTelnyxWebRTC } from '@/hooks/useTelnyxWebRTC';
import { primeDtmfAudio, resumeAllContextsForSyntheticRingback } from '@/lib/dtmfKeypad';
import { ensureCallRingbackLoop, restartRingbackLoopIfPlaying } from '@/lib/callRingbackLoop';
import CallCenterActiveCallCard from '@/components/callCenter/CallCenterActiveCallCard';
import { ErrorBoundarySection } from '@/components/ErrorBoundarySection';
import {
  syncWebRtcStoreFromOutboundCrmInsert,
  tryHydrateOutboundCrmCallFromDb,
} from '@/lib/callCenterDialContext';
import { supabase } from '@/lib/supabase';
import { useAuth, useFeatureFlags } from '@smarty/shared';
import { useStoreNavigate } from '@/hooks/useStoreNavigate';
import { useAdminToast } from '@/contexts/AdminToastContext';
import { ROUTES } from '@/config/routes.config';
import { playCallEndTone } from '@/lib/callEndTone';
import { shouldRunConversationClock } from '@/lib/callCenterPstnConversation';
import { mergeOutboundDestinationPhone, muteRemoteAudioElement, setCrmCallStatusFromRealtime, setActiveCrmCall, clearActiveCallFromRemote as storeClearCall } from '@/lib/telnyxWebRTCStore';
import type { WebrtcCallUiPhase } from '@/lib/telnyxCallState';
import { useCallCenterAgentRemoteSync } from '@/hooks/useCallCenterAgentRemoteSync';
import { useAgentRealtimeChannel } from '@/hooks/useAgentRealtimeChannel';
import { CallDispositionModal, type CallDispositionResult } from '@/components/callCenter/CallDispositionModal';
import { invokeCallCenterStartCall } from '@/lib/callCenter/invokeCallCenterStartCall';

type WebrtcStatus = 'idle' | 'connecting' | 'connected' | 'error';

export interface CallCenterContextValue {
  status: WebrtcStatus;
  error: string | null;
  activeCallId: string | null;
  webrtcUsername: string | null;
  callPhase: WebrtcCallUiPhase;
  callDirection: 'inbound' | 'outbound' | null;
  remoteDialNumber: string | null;
  setDialContext: (phone: string | null) => void;
  connect: () => Promise<void>;
  disconnect: () => void;
  disconnectAndDisable: () => void;
  hangup: () => void;
  clearActiveCallFromRemote: () => void;
  callStartTime: number | null;
  setCallStartTime: (t: number | null) => void;
  elapsedSeconds: number;
  hangupFromUI: () => Promise<void>;
  lastHangupByUsAtRef: React.MutableRefObject<number>;
  /** Colgar en curso (UI). */
  hangingUp: boolean;
  /** Micrófono silenciado. */
  isMuted: boolean;
  /** Llamada en espera (hold). */
  isOnHold: boolean;
  toggleMute: () => void;
  toggleHold: () => void;
  /** Fila `crm_calls` enlazada (manual o llamada automática desde servidor). */
  activeCrmCallId: string | null;
  /** Estado CRM de la llamada activa (`initiated`, `ringing`, `answered`, etc.). */
  crmCallStatus: string | null;
  /**
   * El marcador flotante muestra la tarjeta de llamada integrada (avatar + lead): oculta la tarjeta fija inferior.
   */
  setEmbeddedCallInFloatingDialer: (active: boolean) => void;
  // --- Power Dialer ---
  /** `null` = inactivo. String = campaign_id en curso. */
  powerDialerCampaignId: string | null;
  powerDialerRunning: boolean;
  powerDialerProgress: { done: number; total: number } | null;
  startPowerDialer: (campaignId: string) => void;
  stopPowerDialer: () => void;
  // --- Transferencia ---
  transferCall: (transferTo: string) => Promise<{ success: boolean; message?: string }>;
  warmTransferCall: (action: 'initiate' | 'complete' | 'cancel', transferTo?: string) => Promise<{ success: boolean; message?: string }>;
  warmTransferActive: boolean;
  // --- Grabación ---
  isRecordingPaused: boolean;
  toggleRecordingPause: () => Promise<void>;
}

const CallCenterContext = createContext<CallCenterContextValue | null>(null);

export function useCallCenter() {
  const ctx = useContext(CallCenterContext);
  return ctx;
}

export function CallCenterProvider({ children }: { children: React.ReactNode }) {
  const toast = useAdminToast();
  const storeNavigate = useStoreNavigate();
  const { tenantId, user, loading: authLoading } = useAuth();
  const userId = user?.id ?? null;
  const { isFeatureEnabled, loading: flagsLoading } = useFeatureFlags();
  const lastHangupByUsAtRef = useRef(0);
  const lastRemoteHangupToastAtRef = useRef(0);
  const webhookWarmupDoneRef = useRef(false);
  const prevActiveCallIdRef = useRef<string | null>(null);
  const [callStartTime, setCallStartTime] = useState<number | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  // UI optimista: webrtcHangup() es síncrono e instantáneo, no hay estado de carga.
  const hangingUp = false;
  const [embeddedCallInFloatingDialer, setEmbeddedCallInFloatingDialer] = useState(false);

  // --- Power Dialer ---
  const [pdCampaignId, setPdCampaignId] = useState<string | null>(null);
  const [pdRunning, setPdRunning] = useState(false);
  const [pdProgress, setPdProgress] = useState<{ done: number; total: number } | null>(null);
  const pdStoppedRef = useRef(false);

  // --- Wrap-up / Disposición post-llamada ---
  const [wrapUpOpen, setWrapUpOpen] = useState(false);
  const [wrapUpSubmitting, setWrapUpSubmitting] = useState(false);
  /** Datos de la llamada que acaba de terminar, para el modal de disposición. */
  const wrapUpCallRef = useRef<{
    crmCallId: string | null;
    remotePhone: string | null;
    durationSeconds: number;
    leadName: string | null;
  } | null>(null);

  const {
    status: webrtcStatus,
    error: webrtcError,
    activeCallId: webrtcActiveCallId,
    webrtcUsername,
    callPhase: webrtcCallPhase,
    callDirection: webrtcCallDirection,
    remoteDialNumber: webrtcRemoteDialNumber,
    activeCrmCallId: webrtcActiveCrmCallId,
    crmCallStatus: webrtcCrmCallStatusFromStore,
    setDialContext: webrtcSetDialContext,
    connect: webrtcConnect,
    disconnect: webrtcDisconnect,
    disconnectAndDisable: webrtcDisconnectAndDisable,
    hangup: webrtcHangup,
    clearActiveCallFromRemote: webrtcClearActiveCallFromRemote,
    isMuted: webrtcIsMuted,
    isOnHold: webrtcIsOnHold,
    toggleMute: webrtcToggleMute,
    toggleHold: webrtcToggleHold,
  } = useTelnyxWebRTC();

  const useBrowserForCalls = webrtcStatus === 'connected';

  /**
   * Auto-conectar WebRTC si el módulo call center está habilitado y el usuario ya tiene sesión.
   * Así el agente no necesita activar manualmente “Llamadas por navegador” tras borrar cookies o en un nuevo dispositivo.
   */
  const callCenterEnabled = isFeatureEnabled('crm');
  useEffect(() => {
    if (!callCenterEnabled || authLoading || flagsLoading) return;
    if (!tenantId || !userId) return;
    if (webrtcStatus !== 'idle') return;
    void webrtcConnect();
  }, [callCenterEnabled, authLoading, flagsLoading, tenantId, userId, webrtcStatus, webrtcConnect]);

  /**
   * La llamada **manual** desbloquea Web Audio al pulsar “Llamar”. La **auto-llamada** no: el navegador deja
   * `AudioContext` en `suspended` y el timbrado sintético (“tuuu”) queda en silencio. Cualquier clic o tecla
   * en la app (p. ej. confirmar pedido) ejecuta `resume()` en el mismo contexto que DTMF/ringback.
   */
  useEffect(() => {
    if (!useBrowserForCalls) return;
    const unlock = () => {
      primeDtmfAudio();
      void resumeAllContextsForSyntheticRingback(6, 0);
    };
    window.addEventListener('pointerdown', unlock, true);
    window.addEventListener('keydown', unlock, true);
    return () => {
      window.removeEventListener('pointerdown', unlock, true);
      window.removeEventListener('keydown', unlock, true);
    };
  }, [useBrowserForCalls]);

  /** Timbrado local: bucle en `callRingbackLoop` (store), no en React — evita que re-renders corten el setTimeout. */
  useEffect(() => {
    ensureCallRingbackLoop();
  }, []);

  /**
   * Webhook actualiza `crm_calls` cuando el destino cuelga; el SDK WebRTC a veces no empuja hangup a tiempo.
   * Sincroniza timbrado (initiated/ringing → answered) y fuerza cierre UI en estados finales.
   *
   * Al suscribirse, hace un fetch inicial para cubrir la ventana entre la creación de la llamada
   * y el momento en que `webrtcActiveCrmCallId` se setea (UI optimista: ~1-2s donde Realtime no existe).
   */
  useEffect(() => {
    if (!tenantId || !webrtcActiveCrmCallId) return;

    const TERMINAL_STATUSES = ['ended', 'no_answer', 'busy', 'failed', 'cancelled', 'canceled'];
    let cancelled = false;

    const handleTerminal = (status: string) => {
      setCrmCallStatusFromRealtime(status);
      muteRemoteAudioElement();
      webrtcHangup();
    };

    // Check inicial: si el webhook ya marcó la llamada como terminal antes de que Realtime arranque
    supabase
      .from('crm_calls')
      .select('call_status')
      .eq('id', webrtcActiveCrmCallId)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        const st = ((data as { call_status?: string })?.call_status ?? '').toLowerCase();
        if (st) setCrmCallStatusFromRealtime(st);
        if (TERMINAL_STATUSES.includes(st)) {
          handleTerminal(st);
        }
      });

    // C6: el UPDATE de crm_calls (por id) lo maneja el channel consolidado
    // (useAgentRealtimeChannel → onCrmCallUpdate). C7: el watchdog cada 2.5s
    // como respaldo (era 1.5s constante) y skip si tab oculto. NO chequea
    // isRealtimeConnected acá por orden de hooks; el ahorro grueso ya viene
    // del polling principal en useCallCenterAgentRemoteSync.
    const watchdog = setInterval(() => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      supabase
        .from('crm_calls')
        .select('call_status')
        .eq('id', webrtcActiveCrmCallId)
        .maybeSingle()
        .then(({ data }) => {
          if (cancelled) return;
          const st = ((data as { call_status?: string })?.call_status ?? '').toLowerCase();
          if (TERMINAL_STATUSES.includes(st)) {
            handleTerminal(st);
          }
        });
    }, 2500);

    return () => {
      cancelled = true;
      clearInterval(watchdog);
    };
  }, [tenantId, webrtcActiveCrmCallId, webrtcHangup]);

  const prevPhaseForEndToneRef = useRef<WebrtcCallUiPhase | null>(null);

  /** Pitidos tipo central al pasar de conversación o timbrado a fin de llamada. */
  useEffect(() => {
    const prev = prevPhaseForEndToneRef.current;
    prevPhaseForEndToneRef.current = webrtcCallPhase;
    if (!useBrowserForCalls) return;
    if (prev === null) return;
    // Suena tanto al cortar conversación (active→none) como al rechazar/no contestar (ringing→none).
    if (webrtcCallPhase === 'none' && (prev === 'active' || prev === 'ringing')) {
      playCallEndTone();
    }
  }, [webrtcCallPhase, useBrowserForCalls]);

  const showRemoteHangupToast = useCallback(() => {
    const now = Date.now();
    if (now - lastRemoteHangupToastAtRef.current < 3000) return;
    lastRemoteHangupToastAtRef.current = now;
    toast.success('Llamada colgada por el destinatario');
  }, [toast]);

  /**
   * Cronómetro solo con `crm_calls` y `answered`. Si `activeCrmCallId` aún no llegó, no arranca el reloj
   * (misma regla que la vista Timbrando del FAB — manual y auto usan el mismo componente).
   */
  useEffect(() => {
    if (
      shouldRunConversationClock(
        webrtcCallPhase,
        webrtcActiveCrmCallId,
        webrtcCrmCallStatusFromStore,
      )
    ) {
      setCallStartTime((prev) => prev ?? Date.now());
    } else {
      setCallStartTime(null);
      setElapsedSeconds(0);
    }
  }, [webrtcCallPhase, webrtcActiveCrmCallId, webrtcCrmCallStatusFromStore]);

  useEffect(() => {
    if (!useBrowserForCalls || webrtcCallPhase === 'none' || !tenantId || !userId) return;
    supabase
      .from('call_center_agent_status')
      .upsert(
        {
          tenant_id: tenantId,
          user_id: userId,
          status: 'on_call',
          ...(webrtcUsername?.trim() ? { telnyx_webrtc_login: webrtcUsername.trim() } : {}),
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'tenant_id,user_id' }
      )
      .then(() => {});
  }, [useBrowserForCalls, webrtcCallPhase, tenantId, userId, webrtcUsername]);

  /** Guarda login SIP en BD para enrutar auto-llamadas al agente conectado (Edge Function). No cambia status (available/break). */
  useEffect(() => {
    if (!tenantId || !userId || webrtcStatus !== 'connected' || !webrtcUsername?.trim()) return;
    void supabase.rpc('sync_call_center_webrtc_login', {
      p_tenant_id: tenantId,
      p_user_id: userId,
      p_login: webrtcUsername.trim(),
    });
  }, [tenantId, userId, webrtcStatus, webrtcUsername]);

  useEffect(() => {
    if (callStartTime == null) return;
    const t = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - callStartTime) / 1000));
    }, 1000);
    return () => clearInterval(t);
  }, [callStartTime]);

  // Refs para capturar datos de la llamada antes de que el store los limpie
  const lastCrmCallIdRef = useRef<string | null>(null);
  const lastRemotePhoneRef = useRef<string | null>(null);
  const lastElapsedRef = useRef(0);
  useEffect(() => { lastCrmCallIdRef.current = webrtcActiveCrmCallId; }, [webrtcActiveCrmCallId]);
  useEffect(() => { lastRemotePhoneRef.current = webrtcRemoteDialNumber; }, [webrtcRemoteDialNumber]);
  useEffect(() => { lastElapsedRef.current = elapsedSeconds; }, [elapsedSeconds]);

  useEffect(() => {
    const hadCall = prevActiveCallIdRef.current != null;
    const hasCall = webrtcActiveCallId != null;
    prevActiveCallIdRef.current = webrtcActiveCallId;
    if (useBrowserForCalls && hadCall && !hasCall) {
      // Capturar datos antes de limpiar
      const endedCrmCallId = lastCrmCallIdRef.current;
      const endedPhone = lastRemotePhoneRef.current;
      const endedDuration = lastElapsedRef.current;

      setCallStartTime(null);
      setElapsedSeconds(0);
      const justHungUpByUs = Date.now() - lastHangupByUsAtRef.current < 5000;
      if (!justHungUpByUs) showRemoteHangupToast();

      // Abrir modal de disposición si hubo llamada CRM enlazada
      if (endedCrmCallId) {
        wrapUpCallRef.current = {
          crmCallId: endedCrmCallId,
          remotePhone: endedPhone,
          durationSeconds: endedDuration,
          leadName: null, // el modal lo resuelve internamente si es necesario
        };
        setWrapUpOpen(true);
        // Poner agente en wrap_up
        if (tenantId && userId) {
          void supabase.from('call_center_agent_status').upsert(
            { tenant_id: tenantId, user_id: userId, status: 'wrap_up', updated_at: new Date().toISOString() },
            { onConflict: 'tenant_id,user_id' }
          );
        }
      }
    }
  }, [useBrowserForCalls, webrtcActiveCallId, showRemoteHangupToast, tenantId, userId]);

  const webrtcActiveCrmCallIdRef = useRef(webrtcActiveCrmCallId);
  const webrtcCallPhaseRef = useRef(webrtcCallPhase);
  useEffect(() => {
    webrtcActiveCrmCallIdRef.current = webrtcActiveCrmCallId;
  }, [webrtcActiveCrmCallId]);
  useEffect(() => {
    webrtcCallPhaseRef.current = webrtcCallPhase;
  }, [webrtcCallPhase]);

  /**
   * Sondeo mientras hay pata WebRTC real o fase ≠ none, y sin `activeCrmCallId`.
   * También arranca si `callPhase` aún no pasó a `ringing` pero `activeCallId` ya es del SDK.
   */
  const webrtcHasActiveLeg =
    webrtcCallPhase !== 'none' ||
    (webrtcActiveCallId != null &&
      webrtcActiveCallId !== '' &&
      webrtcActiveCallId !== 'pending');

  useEffect(() => {
    if (!tenantId || !userId || webrtcActiveCrmCallId || !webrtcHasActiveLeg) return;
    let ticks = 0;
    const run = () => {
      if (webrtcActiveCrmCallIdRef.current) return;
      void tryHydrateOutboundCrmCallFromDb({ tenantId, userId });
    };
    run();
    const iv = setInterval(() => {
      ticks += 1;
      if (ticks > 60) {
        clearInterval(iv);
        return;
      }
      run();
    }, 500);
    return () => clearInterval(iv);
  }, [tenantId, userId, webrtcCallPhase, webrtcActiveCallId, webrtcActiveCrmCallId, webrtcHasActiveLeg]);

  /**
   * H1 C6: 1 channel Realtime consolidado por agente (era 3).
   * Maneja:
   *   - INSERT crm_calls → auto-llamada (servidor crea fila → activa pipeline WebRTC)
   *   - UPDATE crm_calls → cambios call_status de la llamada activa (terminal → cuelga)
   *   - UPDATE call_center_agent_status → si servidor marca 'available' (otro cliente
   *     colgó), limpia call local + toast.
   */
  const { isRealtimeConnected } = useAgentRealtimeChannel(tenantId, userId, {
    onCrmCallInsert: (row) => {
      const dest = row.destination_phone?.trim();
      syncWebRtcStoreFromOutboundCrmInsert({
        destinationPhone: dest || null,
        crmCallId: row.id,
        crmStatus: row.call_status ?? 'pending',
      });
      // A veces el payload INSERT de Realtime llega sin `destination_phone` (replica/serialización).
      // Sin número, el store usa el caller del SDK y el CRM no matchea.
      if (!dest) {
        void supabase
          .from('crm_calls')
          .select('destination_phone')
          .eq('id', row.id)
          .eq('tenant_id', tenantId!)
          .maybeSingle()
          .then(({ data }) => {
            const p = (data as { destination_phone?: string | null } | null)?.destination_phone?.trim();
            if (p) mergeOutboundDestinationPhone(p);
          });
      }
      // Sin clic en "Llamar": forzar `running` y reiniciar bucle (Realtime no es gesto de audio).
      primeDtmfAudio();
      void resumeAllContextsForSyntheticRingback(18, 45).then(() => {
        restartRingbackLoopIfPlaying();
      });
    },
    onCrmCallUpdate: (row) => {
      // Solo procesar si es la call activa.
      if (row.id !== webrtcActiveCrmCallIdRef.current) return;
      const TERMINAL_STATUSES = ['ended', 'no_answer', 'busy', 'failed', 'cancelled', 'canceled'];
      const st = (row.call_status ?? '').toLowerCase();
      setCrmCallStatusFromRealtime(st || null);
      if (TERMINAL_STATUSES.includes(st)) {
        muteRemoteAudioElement();
        webrtcHangup();
      }
    },
    onAgentStatusUpdate: (row) => {
      if (row.status !== 'available') return;
      webrtcClearActiveCallFromRemote();
      setCallStartTime(null);
      const justHungUpByUs = Date.now() - lastHangupByUsAtRef.current < 5000;
      if (!justHungUpByUs) showRemoteHangupToast();
    },
  });

  // H1 C7: polling fallback adaptativo (2s en lugar de 400ms; skip si Realtime ok o tab oculto).
  useCallCenterAgentRemoteSync({
    tenantId,
    userId,
    webrtcCallPhase,
    isRealtimeConnected,
    clearActiveCallFromRemote: webrtcClearActiveCallFromRemote,
    setCallStartTime,
    showRemoteHangupToast,
    lastHangupByUsAtRef,
  });

  useEffect(() => {
    if (!useBrowserForCalls || webhookWarmupDoneRef.current) return;
    webhookWarmupDoneRef.current = true;
    supabase.functions
      .invoke('webhook-voice-provider', {
        body: { data: { event_type: 'call.warmup', call_control_id: 'warmup-' + Date.now() } },
      })
      .catch(() => {});
  }, [useBrowserForCalls]);

  const hangupFromUI = useCallback(async () => {
    lastHangupByUsAtRef.current = Date.now();

    // UI optimista: colgar WebRTC de inmediato para que la vista vuelva a idle instantáneamente.
    // La Edge Function corre en background para colgar las patas en Telnyx + limpiar CRM.
    if (useBrowserForCalls) {
      webrtcHangup();
    }
    setCallStartTime(null);
    setIsRecordingPaused(false);
    setWarmTransferActive(false);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke('call-center-hangup', {
        headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : undefined,
      });
      const res = (data ?? {}) as { success?: boolean; error?: string; message?: string };
      const msg = res?.message || res?.error || (error?.message ?? '');
      const isCallAlreadyEnded = /no longer active|can't receive commands/i.test(msg);
      const isNoRowInCrm =
        res?.error === 'Sin llamada activa' ||
        /no hay ninguna llamada en curso/i.test(msg) ||
        /sin llamada activa/i.test(msg);

      if (!isCallAlreadyEnded && !isNoRowInCrm && (error || !res?.success)) {
        const hint = error ? ' Despliega en Supabase la función call-center-hangup.' : '';
        toast.error(res?.error ?? 'Error', msg + hint);
        return;
      }
      // No poner 'available' aquí: el useEffect de fin de llamada abre wrap-up.
      // Si no hay CRM call (raro), poner available directamente.
      if (tenantId && userId) {
        await supabase.from('call_center_agent_status').upsert(
          { tenant_id: tenantId, user_id: userId, status: 'wrap_up', updated_at: new Date().toISOString() },
          { onConflict: 'tenant_id,user_id' }
        );
      }
    } catch (e) {
      toast.error('Error', e instanceof Error ? e.message : 'No se pudo colgar.');
    }
  }, [tenantId, userId, toast, useBrowserForCalls, webrtcHangup]);

  // --- Handlers de disposición post-llamada ---
  const handleDispositionSubmit = useCallback(async (result: CallDispositionResult) => {
    const wrapUp = wrapUpCallRef.current;
    const callId = wrapUp?.crmCallId;
    if (!callId) { setWrapUpOpen(false); return; }
    setWrapUpSubmitting(true);
    try {
      // 1) Guardar disposición en crm_calls
      await supabase.from('crm_calls').update({
        disposition: result.disposition,
        disposition_notes: result.notes || null,
        updated_at: new Date().toISOString(),
      }).eq('id', callId);

      // 2) Si es callback, crear actividad scheduled_call en crm_activities
      if (result.disposition === 'callback' && result.callbackAt && tenantId) {
        // Obtener lead_id desde la llamada
        const { data: callRow } = await supabase
          .from('crm_calls')
          .select('lead_id')
          .eq('id', callId)
          .maybeSingle();
        const leadId = (callRow as { lead_id?: string | null } | null)?.lead_id;
        if (leadId) {
          await supabase.from('crm_activities').insert({
            tenant_id: tenantId,
            lead_id: leadId,
            type: 'scheduled_call',
            content: result.notes || 'Rellamada agendada desde disposición',
            scheduled_at: result.callbackAt,
            created_by: userId,
          });
        }
      }
      // 3) Si la llamada tiene queue_task_id, marcar como completado
      if (tenantId) {
        const { data: callForTask } = await supabase
          .from('crm_calls')
          .select('queue_task_id')
          .eq('id', callId)
          .maybeSingle();
        const queueTaskId = (callForTask as { queue_task_id?: string | null } | null)?.queue_task_id;
        if (queueTaskId) {
          await supabase.from('call_center_queue_tasks').update({
            status: 'completed',
            updated_at: new Date().toISOString(),
          }).eq('id', queueTaskId);
        }
      }
    } catch { /* best-effort */ }
    // Poner agente disponible
    if (tenantId && userId) {
      await supabase.from('call_center_agent_status').upsert(
        { tenant_id: tenantId, user_id: userId, status: 'available', updated_at: new Date().toISOString() },
        { onConflict: 'tenant_id,user_id' }
      );
      // Trigger queue processing — assign next queued call to this agent
      supabase.functions.invoke('call-center-process-queue', {
        body: { tenant_id: tenantId, agent_id: userId },
      }).catch(() => {});
    }
    setWrapUpSubmitting(false);
    setWrapUpOpen(false);
    wrapUpCallRef.current = null;
  }, [tenantId, userId]);

  const handleDispositionSkip = useCallback(async () => {
    setWrapUpOpen(false);
    wrapUpCallRef.current = null;
    if (tenantId && userId) {
      await supabase.from('call_center_agent_status').upsert(
        { tenant_id: tenantId, user_id: userId, status: 'available', updated_at: new Date().toISOString() },
        { onConflict: 'tenant_id,user_id' }
      );
      supabase.functions.invoke('call-center-process-queue', {
        body: { tenant_id: tenantId, agent_id: userId },
      }).catch(() => {});
    }
  }, [tenantId, userId]);

  // --- Power Dialer: dial next pending task ---
  const dialNextInCampaign = useCallback(async (campaignId: string) => {
    if (!tenantId || !userId || pdStoppedRef.current) return;
    // Fetch progress
    const { count: totalCount } = await supabase
      .from('call_center_queue_tasks')
      .select('id', { count: 'exact', head: true })
      .eq('tenant_id', tenantId)
      .eq('campaign_id', campaignId);
    const { count: doneCount } = await supabase
      .from('call_center_queue_tasks')
      .select('id', { count: 'exact', head: true })
      .eq('tenant_id', tenantId)
      .eq('campaign_id', campaignId)
      .in('status', ['completed', 'cancelled']);
    setPdProgress({ done: doneCount ?? 0, total: totalCount ?? 0 });

    // Pick next pending task
    const { data: nextTask } = await supabase
      .from('call_center_queue_tasks')
      .select('id, lead_id')
      .eq('tenant_id', tenantId)
      .eq('campaign_id', campaignId)
      .eq('status', 'pending')
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle();

    if (!nextTask || pdStoppedRef.current) {
      // Campaign done
      setPdRunning(false);
      setPdCampaignId(null);
      setPdProgress(null);
      toast.success('Power Dialer', 'Campaña completada — no quedan leads pendientes.');
      return;
    }

    // Mark task as calling
    await supabase.from('call_center_queue_tasks').update({
      status: 'calling',
      assigned_agent_id: userId,
      updated_at: new Date().toISOString(),
    }).eq('id', nextTask.id);

    // Get lead phone
    const { data: lead } = await supabase
      .from('crm_leads')
      .select('id, phone')
      .eq('id', nextTask.lead_id)
      .maybeSingle();
    const phone = (lead as { phone?: string | null } | null)?.phone;
    if (!phone) {
      // Skip — mark as cancelled and move on
      await supabase.from('call_center_queue_tasks').update({
        status: 'cancelled', updated_at: new Date().toISOString(),
      }).eq('id', nextTask.id);
      // Recurse to next
      void dialNextInCampaign(campaignId);
      return;
    }

    // Initiate call
    syncWebRtcStoreFromOutboundCrmInsert({
      destinationPhone: phone,
      crmCallId: null,
      crmStatus: 'initiated',
    });

    const out = await invokeCallCenterStartCall({
      body: { lead_id: nextTask.lead_id, campaign_id: campaignId, queue_task_id: nextTask.id },
      useWebrtc: webrtcStatus === 'connected',
      webrtcUsername: webrtcUsername,
    });

    if (!out.ok) {
      storeClearCall();
      toast.error('title' in out ? out.title : 'Error', 'detail' in out ? out.detail : 'Error al iniciar llamada');
      // Mark task as cancelled and try next
      await supabase.from('call_center_queue_tasks').update({
        status: 'cancelled', updated_at: new Date().toISOString(),
      }).eq('id', nextTask.id);
      if (!pdStoppedRef.current) void dialNextInCampaign(campaignId);
      return;
    }

    if (out.call_id) {
      setActiveCrmCall(out.call_id, 'initiated', { forceBusinessOutbound: true });
    }
    // Mark task as assigned (call initiated successfully)
    await supabase.from('call_center_queue_tasks').update({
      status: 'assigned', updated_at: new Date().toISOString(),
    }).eq('id', nextTask.id);
  }, [tenantId, userId, webrtcStatus, webrtcUsername, toast]);

  const startPowerDialer = useCallback((campaignId: string) => {
    pdStoppedRef.current = false;
    setPdCampaignId(campaignId);
    setPdRunning(true);
    void dialNextInCampaign(campaignId);
  }, [dialNextInCampaign]);

  const stopPowerDialer = useCallback(() => {
    pdStoppedRef.current = true;
    setPdRunning(false);
    setPdCampaignId(null);
    setPdProgress(null);
  }, []);

  // After disposition + wrap-up completes, auto-dial next if power dialer active
  const pdCampaignIdRef = useRef(pdCampaignId);
  const pdRunningRef = useRef(pdRunning);
  useEffect(() => { pdCampaignIdRef.current = pdCampaignId; }, [pdCampaignId]);
  useEffect(() => { pdRunningRef.current = pdRunning; }, [pdRunning]);

  // Watch for wrap-up closing → trigger next dial
  useEffect(() => {
    if (wrapUpOpen) return; // only trigger when it closes
    if (!pdRunningRef.current || !pdCampaignIdRef.current) return;
    // Small delay to let UI settle
    const t = setTimeout(() => {
      if (pdRunningRef.current && pdCampaignIdRef.current && !pdStoppedRef.current) {
        void dialNextInCampaign(pdCampaignIdRef.current);
      }
    }, 1500);
    return () => clearTimeout(t);
  }, [wrapUpOpen, dialNextInCampaign]);

  const transferCall = useCallback(async (transferTo: string): Promise<{ success: boolean; message?: string }> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke('call-center-transfer-call', {
        headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : undefined,
        body: { transfer_to: transferTo },
      });
      const res = (data ?? {}) as { success?: boolean; error?: string; message?: string };
      if (error || !res.success) {
        const msg = res?.message || res?.error || error?.message || 'Error al transferir.';
        toast.error('Error', msg);
        return { success: false, message: msg };
      }
      // Clear local call state
      if (useBrowserForCalls) webrtcHangup();
      setCallStartTime(null);
      toast.success('Transferencia', res.message || 'Llamada transferida.');
      return { success: true, message: res.message };
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Error al transferir.';
      toast.error('Error', msg);
      return { success: false, message: msg };
    }
  }, [toast, useBrowserForCalls, webrtcHangup]);

  const [warmTransferActive, setWarmTransferActive] = useState(false);

  const warmTransferCall = useCallback(async (action: 'initiate' | 'complete' | 'cancel', transferTo?: string): Promise<{ success: boolean; message?: string }> => {
    const callId = webrtcActiveCrmCallId;
    if (!callId) {
      toast.error('Error', 'No hay llamada activa.');
      return { success: false, message: 'No hay llamada activa.' };
    }
    try {
      const { data, error } = await supabase.functions.invoke('call-center-warm-transfer', {
        body: { crm_call_id: callId, action, transfer_to: transferTo },
      });
      const res = (data ?? {}) as { success?: boolean; error?: string; message?: string };
      if (error || !res.success) {
        const msg = res?.error || error?.message || 'Error en transferencia asistida.';
        toast.error('Error', msg);
        return { success: false, message: msg };
      }
      if (action === 'initiate') {
        setWarmTransferActive(true);
        toast.success('Transferencia asistida', 'Consultando con destino. Completa o cancela cuando estés listo.');
      } else if (action === 'complete') {
        setWarmTransferActive(false);
        if (useBrowserForCalls) webrtcHangup();
        setCallStartTime(null);
        toast.success('Transferencia', 'Transferencia asistida completada.');
      } else if (action === 'cancel') {
        setWarmTransferActive(false);
        toast.success('Transferencia', 'Transferencia cancelada. Volviste con el cliente.');
      }
      return { success: true };
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Error en transferencia asistida.';
      toast.error('Error', msg);
      return { success: false, message: msg };
    }
  }, [toast, webrtcActiveCrmCallId, useBrowserForCalls, webrtcHangup]);

  const [isRecordingPaused, setIsRecordingPaused] = useState(false);

  const toggleRecordingPause = useCallback(async () => {
    const callId = webrtcActiveCrmCallId;
    if (!callId) return;
    const newAction = isRecordingPaused ? 'resume' : 'pause';
    try {
      const { data, error } = await supabase.functions.invoke('call-center-recording-control', {
        body: { crm_call_id: callId, action: newAction },
      });
      const res = (data ?? {}) as { success?: boolean; error?: string };
      if (error || !res.success) {
        toast.error('Grabación', res?.error || error?.message || `Error al ${newAction === 'pause' ? 'pausar' : 'reanudar'} grabación.`);
        return;
      }
      setIsRecordingPaused(!isRecordingPaused);
      toast.success('Grabación', newAction === 'pause' ? 'Grabación pausada.' : 'Grabación reanudada.');
    } catch (e) {
      toast.error('Grabación', e instanceof Error ? e.message : 'Error de grabación.');
    }
  }, [toast, webrtcActiveCrmCallId, isRecordingPaused]);

  const value: CallCenterContextValue = {
    status: webrtcStatus,
    error: webrtcError,
    activeCallId: webrtcActiveCallId,
    webrtcUsername,
    callPhase: webrtcCallPhase,
    callDirection: webrtcCallDirection,
    remoteDialNumber: webrtcRemoteDialNumber,
    setDialContext: webrtcSetDialContext,
    connect: webrtcConnect,
    disconnect: webrtcDisconnect,
    disconnectAndDisable: webrtcDisconnectAndDisable,
    hangup: webrtcHangup,
    clearActiveCallFromRemote: webrtcClearActiveCallFromRemote,
    callStartTime,
    setCallStartTime,
    elapsedSeconds,
    hangupFromUI,
    lastHangupByUsAtRef,
    hangingUp,
    activeCrmCallId: webrtcActiveCrmCallId,
    crmCallStatus: webrtcCrmCallStatusFromStore,
    setEmbeddedCallInFloatingDialer,
    isMuted: webrtcIsMuted,
    isOnHold: webrtcIsOnHold,
    toggleMute: () => { webrtcToggleMute(); },
    toggleHold: () => { webrtcToggleHold(); },
    powerDialerCampaignId: pdCampaignId,
    powerDialerRunning: pdRunning,
    powerDialerProgress: pdProgress,
    startPowerDialer,
    stopPowerDialer,
    transferCall,
    warmTransferCall,
    warmTransferActive,
    isRecordingPaused,
    toggleRecordingPause,
  };

  /** Mismas condiciones que `FloatingDialerWidget`: si el FAB CRM existe, nunca la tarjeta esquina (evita duplicado). */
  const floatingDialerHandlesCallUi =
    !flagsLoading &&
    !authLoading &&
    isFeatureEnabled('crm') &&
    tenantId != null &&
    userId != null;

  const showActiveCallCard =
    useBrowserForCalls &&
    webrtcCallPhase !== 'none' &&
    tenantId != null &&
    !floatingDialerHandlesCallUi &&
    !embeddedCallInFloatingDialer;

  return (
    <CallCenterContext.Provider value={value}>
      {showActiveCallCard && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            /* Por encima del marcador flotante (FAB/panel ~10050) para que avatar + datos del lead se vean al llamar */
            zIndex: 10150,
            pointerEvents: 'auto',
            maxWidth: 'min(420px, calc(100vw - 32px))',
          }}
        >
          {/* H1 I4: aislar errores de la session activa para no tumbar el provider entero. */}
          <ErrorBoundarySection sectionName="Llamada activa" fallback={null}>
            <CallCenterActiveCallCard
              tenantId={tenantId!}
              crmCallId={webrtcActiveCrmCallId}
              crmCallStatus={webrtcCrmCallStatusFromStore}
              remotePhone={webrtcRemoteDialNumber}
              callPhase={webrtcCallPhase}
              callDirection={webrtcCallDirection}
              elapsedSeconds={elapsedSeconds}
              onHangup={() => hangupFromUI()}
              onOpenDialer={() => storeNavigate(ROUTES.APPS_CRM_DIALER)}
              hangingUp={hangingUp}
              isMuted={webrtcIsMuted}
              isOnHold={webrtcIsOnHold}
              onToggleMute={() => { webrtcToggleMute(); }}
              onToggleHold={() => { webrtcToggleHold(); }}
              onTransfer={transferCall}
              onWarmTransfer={warmTransferCall}
              warmTransferActive={warmTransferActive}
              isRecordingPaused={isRecordingPaused}
              onToggleRecordingPause={toggleRecordingPause}
            />
          </ErrorBoundarySection>
        </div>
      )}
      {children}
      <CallDispositionModal
        open={wrapUpOpen}
        remotePhone={wrapUpCallRef.current?.remotePhone ?? null}
        leadName={wrapUpCallRef.current?.leadName ?? null}
        durationSeconds={wrapUpCallRef.current?.durationSeconds ?? 0}
        onSubmit={handleDispositionSubmit}
        onSkip={handleDispositionSkip}
        submitting={wrapUpSubmitting}
      />
    </CallCenterContext.Provider>
  );
}
